<template>
	<card :title="options.title || report.type">
		<template #default>
			<div v-if="editing">
				<div class="form-row mb-4">
					<o-field label="Date filter">
						<o-select v-model="activeReport.filter.type" expanded class="date-filter-type">
							<option v-for="option in timeTypes" :key="option.Id" :value="option.Id">
								{{ option.Name }}
							</option>
						</o-select>
					</o-field>
				</div>
				<div v-if="activeReport.filter.type != 'none'" class="mb-4">
					<o-field label="Date range">
						<date-filter
							v-model="activeReport.filter.date"
							:reportType="activeReport.type"></date-filter>
					</o-field>
				</div>
				<div class="form-row">
					<search-filter v-model="activeReport.filter.keys" :type="filterType"></search-filter>

					<o-button
						class="runReportBtn"
						variant="primary"
						icon-left="cog"
						:loading="report.cancelToken ? false : report.loading"
						:disabled="report.cancelToken ? false : report.loading"
						@click="GetReport">
						Generate Report
					</o-button>
				</div>
				<div class="form-row">
					<slot></slot>
				</div>
			</div>
			<div v-else>
				<div class="is-flex is-justify-content-space-between">
					<div class="is-flex is-flex-direction-column">
						<div>
							<o-field :label="timeTypeLabel">
								{{
									moment.utc(report.filter.date.startDate).format("MM/DD/YYYY") +
									" - " +
									moment.utc(report.filter.date.endDate).format("MM/DD/YYYY")
								}}
							</o-field>
						</div>

						<div>
							<o-field label="Filters" v-if="report.filter.keys && report.filter.keys.length > 0">
								<div
									class="tag mr-2 my-2 normal-font-size"
									v-for="(kv, index) in report.filter.keys"
									:key="index">
									<span class="mr-2">{{ kv.key?.Text }}:</span>
									<span class="mr-2">
										{{ kv.value?.Text }}
									</span>
								</div>
							</o-field>
						</div>
					</div>
					<div class="is-flex is-flex-direction-column">
						<div class="mb-4">
							<o-field label="Report generated from cache" v-if="report.lastCached">
								<div class="mr-2">{{ report.lastCached.local().format("MM/DD/YYYY h:mm a") }}</div>
								<o-button
									class="refreshBtn"
									:disabled="report.loading"
									size="small"
									icon-left="sync-alt"
									@click="RefreshCache">
									Refresh
								</o-button>
							</o-field>
						</div>
						<div class="form-row form-row-right">
							<o-button class="modifyBtn" variant="primary" icon-left="pencil" @click="EditReport">
								Modify Report
							</o-button>
						</div>
					</div>
				</div>
				<div>
					<slot></slot>
				</div>
			</div>
		</template>
		<template #actions>
			<slot name="actions"></slot>
			<share
				class="is-pulled-right"
				:report="report"
				:exporting="exporting"
				:hide="options.share === false || !report.results?.length"
				:excel="
					(options.excel && options.headers && options.headers.length > 0) ||
					(options.excel && Array.isArray(options.sheets))
				"
				@excel="Export"></share>
		</template>
	</card>
</template>
<script>
	import { ref, computed, watch, inject } from "vue";
	import { share, dateFilter, searchFilter } from "@/components/webComponents";

	export default {
		components: {
			share,
			dateFilter,
			searchFilter
		},
		props: {
			report: {
				type: Object,
				default: () => {
					return { filter: {} };
				}
			},
			editing: {
				type: Boolean,
				default: false
			},
			autoRun: {
				type: Boolean,
				default: false
			},
			options: {
				type: Object,
				default: () => {
					return {};
				}
			}
		},
		emits: ["search", "edit", "beforeExport", "update:report"],
		setup(props, context) {
			const $http = inject("$http");
			const moment = inject("$moment");
			const $error = inject("$error");

			const exporting = ref(false);

			const filterType = computed(() => {
				switch (props.report.type) {
					case "Assignment":
						return "assignments";
					case "Company":
						return "companies";
					case "Financial":
						return "financial";
					case "User":
						return "users";
					case "License":
						return "licenses";
					case "Location":
						return "locations";
					default:
						return "none";
				}
			});
			const timeTypes = computed(() => {
				switch (props.report.type) {
					case "Assignment":
						return [
							{ Id: "activity", Name: "Activity" },
							{ Id: "taken", Name: "Date Taken" },
							{ Id: "invoice", Name: "Invoice Date" },
							{ Id: "status", Name: "Status Date" },
							{ Id: "created", Name: "Created Date" },
							{ Id: "markedfilled", Name: "Marked or Filled Date" }
						];
					case "Company":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "activity", Name: "Activity" },
							{ Id: "noActivity", Name: "No Activity" },
							{ Id: "invoice", Name: "Invoice" },
							{ Id: "taken", Name: "Assignment Taken" }
						];
					case "Financial":
						return [
							{ Id: "activity", Name: "Activity" },
							{ Id: "taken", Name: "Date Taken" },
							{ Id: "invoice", Name: "Invoice Date" },
							{ Id: "status", Name: "Status Date" }
						];
					case "User":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					case "License":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					case "Location":
						return [
							{ Id: "none", Name: "None" },
							{ Id: "hire", Name: "Hire Date" },
							{ Id: "inactive", Name: "Inactive Date" },
							{ Id: "role", Name: "Role Changed" },
							{ Id: "roleORhire", Name: "Role Changed or Hire Date" }
						];
					default:
						return [];
				}
			});
			const timeTypeLabel = computed(() => {
				return timeTypes.value.find((x) => x.Id == props.report.filter.type)?.Name || "None";
			});
			const activeReport = ref(props.report);

			// const DeleteCache = async () => {
			// 	try {
			// 		const copy = JSON.parse(JSON.stringify(props.report));

			// 		copy.results = [];
			// 		copy.compare = [];

			// 		await $http.delete("/api/v2/report/cache", { data: { report: copy } });
			// 	} catch (err) {
			// 		console.error(err);
			// 	}
			// };

			const GetReport = () => {
				context.emit("search");
			};

			const RefreshCache = async () => {
				//await DeleteCache();
				context.emit("search", { refresh: true });
			};

			const EditReport = () => {
				if (activeReport.value.cancelToken) activeReport.value.cancelToken.cancel("Editing.");
				context.emit("edit");
			};

			const SyncReport = () => {
				//context.emit("update:report", activeReport.value);
				activeReport.value.filter = props.report.filter;
			};

			const Export = async () => {
				exporting.value = true;
				context.emit("beforeExport");
				try {
					const filename =
						moment().format("MMDDYYYY-hhmm") + " " + props.report.type + " report.xlsx";
					const response = await $http.post(
						"/api/v2/report/xlsx",
						{
							sheets: props.options.sheets || [
								{
									title: props.report.type,
									headers: props.options.headers || [],
									rows: props.report.results
								}
							]
						},
						{ responseType: "blob" }
					);

					const blob = response.data;
					const link = document.createElement("a");
					link.href = window.URL.createObjectURL(blob);
					link.setAttribute("download", filename);

					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} catch (err) {
					$error("Error: Exporting timed out.");
					console.error(err);
				}
				exporting.value = false;
			};

			let hasRan = false;
			watch(
				() => props.report.filter,
				(n, o) => {
					SyncReport();

					if (props.autoRun && !hasRan) {
						hasRan = true;
						GetReport();
					}
				},
				{ deep: true, immediate: true }
			);

			return {
				moment,
				exporting,
				filterType,
				timeTypes,
				timeTypeLabel,
				activeReport,
				GetReport,
				EditReport,
				RefreshCache,
				Export
			};
		}
	};
</script>
<style lang="scss" scoped>
	@import "@/assets/css/globalVars.scss";

	.normal-font-size {
		font-size: 1rem;
	}

	.form-row {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: flex-start;
		justify-content: flex-start;
		gap: $margin-element-medium;
		width: 100%;

		&:nth-child(2) {
			align-items: flex-end;
			justify-content: space-between;
		}
	}

	.form-row-right {
		justify-content: flex-end;
		align-items: flex-end;

		&:nth-child(2) {
			align-items: flex-end;
			justify-content: flex-end;
		}
	}
</style>
